import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { MdMoreVert, MdModeEdit, MdFilePresent } from "react-icons/md";
import { IoChevronDown } from "react-icons/io5";
import { Bar, CartesianGrid, XAxis, YAxis, Tooltip as RechartsTooltip, Legend, ResponsiveContainer, ComposedChart } from "recharts";

import Switch from "../../../components/Switch";
import api from "../../../services/api";
import Modal from "../../../components/modal";
import SelectMonth from "../../../components/selectMonth";
import EditModal from "./budgetModal";
import CreateModal from "./budgetCreate";
import { classNames } from "../../../utils";

export default function BudgetUnified({ project }) {
  return <BudgetListPage project={project} />;
}

// =====================================================
// LIST PAGE
// =====================================================

function BudgetListPage({ project }) {
  const [filters, setFilters] = useState({ inactive: false });
  const [data, setData] = useState([]);
  const [editBudget, setEditBudget] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [activities, setActivities] = useState([]);
  const [banks, setBanks] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [budgetSelected, setBudgetSelected] = useState(null);
  const [vizOpen, setVizOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const user = useSelector((state) => state.Auth.user);

  useEffect(() => {
    fetchData();
  }, [project, filters]);

  const fetchData = async () => {
    try {
      const query = { projectId: project._id };
      if (!filters.inactive) query.status = "active";
      const res = await api.post(`/budget/search`, query);
      if (!res.ok) return toast.error("Failed to fetch budgets");
      setData(res.data);
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch data");
    }
  };

  useEffect(() => {
    if (!data.length) {
      setActivities([]);
      setBanks([]);
      setInvoices([]);
      return;
    }
    const fetchRelated = async () => {
      try {
        const budgetIds = data.map((e) => e._id);
        const [aRes, bRes, iRes] = await Promise.all([
          api.post(`/activity/search`, { projectId: project._id, budgetIds }),
          api.post(`/bank/search`, { projectId: project._id, budgetIds, notCategories: ["INCOME", "DISBURSEMENT"], per_page: 20000 }),
          api.post(`/invoice/search`, { projectId: project._id, budgetIds, per_page: 20000 }),
        ]);
        setActivities(aRes.data || []);
        setBanks((bRes.data?.banks || []).map((bank) => ({ ...bank, amount: bank.amount })));
        setInvoices(iRes.data || []);
      } catch (error) {
        console.error(error);
        toast.error("Failed to fetch data");
      }
    };
    fetchRelated();
  }, [data]);

  const rows = data.map((budget) => ({
    budget,
    activities: activities.filter((a) => a.budgetId === budget._id),
    banks: banks.filter((b) => b.budgetId === budget._id),
    invoices: invoices.filter((i) => i.budgetId === budget._id),
  }));

  const computeProfit = (item) => {
    const invoiced = item.invoices.reduce((acc, cur) => acc + cur.netTotal, 0);
    const userCost = item.activities.reduce((acc, cur) => acc + cur.cost, 0);
    const bankCost = item.banks.filter((bank) => bank.category !== "INCOME" && bank.category !== "DISBURSEMENT").reduce((acc, bank) => acc - bank.amount, 0);
    const realCost = userCost + bankCost;
    if (item.budget.status === "inactive" || (item.budget.type !== "project" && item.budget.type !== "anticipation")) return invoiced - realCost;
    if (item.budget.amountUsed > item.budget.amount) return item.budget.amount - realCost;
    return item.budget.amountUsed - realCost;
  };

  // Deal state is derived: signed_at → won, lost → lost, otherwise open pipeline.
  const dealStatusOf = (budget) => (budget.signed_at ? "won" : budget.lost ? "lost" : "open");
  const wonRows = rows.filter((r) => dealStatusOf(r.budget) === "won");
  const openRows = rows.filter((r) => dealStatusOf(r.budget) === "open");
  const lostRows = rows.filter((r) => dealStatusOf(r.budget) === "lost");

  const wonBudgetIds = new Set(wonRows.map((r) => r.budget._id));
  const wonActivities = activities.filter((a) => wonBudgetIds.has(a.budgetId));
  const wonBanks = banks.filter((b) => wonBudgetIds.has(b.budgetId));
  const wonData = data.filter((b) => dealStatusOf(b) === "won");

  const monthlyData = calculateMonthlyData(wonData, wonActivities, wonBanks);
  const activitiesByUser = buildActivitiesByUser(wonActivities);
  const banksByName = buildBanksByName(wonBanks);
  const isAdmin = ["super_admin", "admin", "user"].includes(user.role);

  return (
    <div className="space-y-4 py-6">
      {/* Header */}
      <div className="flex items-center gap-3 justify-between px-3">
        <h2 className="text-lg font-semibold">{project.name} - Budget Overview</h2>
        <div className="flex items-center gap-3">
          <EditModal budget={editBudget} onClose={() => setEditBudget(null)} onDone={fetchData} />
          <label className="text-sm font-semibold">Show Inactive</label>
          <Switch checked={filters.inactive} onChange={() => setFilters({ ...filters, inactive: !filters.inactive })} />
          <CreateModal
            project={project}
            onDone={(budget) => {
              setData([...data, budget]);
              setEditBudget(budget);
            }}
          />
        </div>
      </div>

      <BudgetSection
        title="Won"
        description="Deals signed and being delivered"
        rows={wonRows}
        isAdmin={isAdmin}
        setEditBudget={setEditBudget}
        setModalOpen={setModalOpen}
        setBudgetSelected={setBudgetSelected}
        computeProfit={computeProfit}
      />

      <BudgetSectionConfidence
        title="Open"
        description="Active opportunities — proposal sent, awaiting client decision"
        rows={openRows}
        isAdmin={isAdmin}
        setEditBudget={setEditBudget}
        setModalOpen={setModalOpen}
        setBudgetSelected={setBudgetSelected}
        computeProfit={computeProfit}
      />

      <BudgetSectionConfidence
        title="Lost"
        description="Deals declined or that did not close"
        rows={lostRows}
        isAdmin={isAdmin}
        setEditBudget={setEditBudget}
        setModalOpen={setModalOpen}
        setBudgetSelected={setBudgetSelected}
        computeProfit={computeProfit}
      />

      {/* Collapsible: Budget Visualization */}
      <CollapsibleSection icon="📊" title="Budget Visualization" open={vizOpen} onToggle={() => setVizOpen(!vizOpen)}>
        <BudgetChart monthlyData={monthlyData} />
      </CollapsibleSection>

      {/* Collapsible: Detailed Activities */}
      <CollapsibleSection icon="👥" title="Detailed Activities" open={detailsOpen} onToggle={() => setDetailsOpen(!detailsOpen)}>
        <ActivitiesAndBanks activitiesByUser={activitiesByUser} banksByName={banksByName} />
      </CollapsibleSection>

      <GenerateInvoiceModal modalOpen={modalOpen} setModalOpen={setModalOpen} budgetSelected={budgetSelected} />
    </div>
  );
}

// =====================================================
// SHARED COMPONENTS
// =====================================================

const CollapsibleSection = ({ icon, title, open, onToggle, children }) => (
  <div className="bg-white border border-gray-300 rounded">
    <button className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors" onClick={onToggle}>
      <div className="flex items-center gap-2 font-semibold">
        <span>{icon}</span>
        <span>{title}</span>
      </div>
      <IoChevronDown className={`w-5 h-5 text-gray-500 transition-transform ${open ? "rotate-180" : ""}`} />
    </button>
    {open && <div className="p-4 border-t border-gray-200">{children}</div>}
  </div>
);

const BudgetSection = ({ title, description, rows, isAdmin, setEditBudget, setModalOpen, setBudgetSelected, computeProfit }) => (
  <div className="space-y-3">
    <div className="flex items-baseline gap-2 px-1">
      <h3 className="text-base font-semibold text-gray-700">{title}</h3>
      {description && <span className="text-xs text-gray-500">— {description}</span>}
    </div>

    <div className="w-full bg-white border border-gray-300 rounded">
      <table className="table-fixed w-full text-sm">
        <thead className="border-b border-gray-300">
          <tr>
            <th className="text-left font-semibold text-sm p-3 w-[22%]">Budget</th>
            <th className="text-left font-semibold text-sm px-3 w-[8%]">Type</th>
            <th className="text-left font-semibold text-sm p-2 w-[10%]">Amount</th>
            <th className="text-left font-semibold text-sm px-3 w-[10%]">Used</th>
            {isAdmin && <th className="text-left font-semibold text-sm px-3 w-[12%]">Real Cost</th>}
            {isAdmin && <th className="text-left font-semibold text-sm px-3 w-[10%]">Profitability</th>}
            <th className="text-left font-semibold text-sm px-3 w-[8%]">Status</th>
            <th className="text-left font-semibold text-sm px-3 w-[14%]">Progress</th>
            {isAdmin && <th className="text-left font-semibold text-sm px-3 w-[6%]"></th>}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={isAdmin ? 9 : 7} className="p-4 text-sm text-gray-500 text-center">No {title.toLowerCase()} budgets</td>
            </tr>
          ) : (
            rows.map((row, index) => (
              <BudgetRow
                key={index}
                item={row}
                isAdmin={isAdmin}
                onNavigate={() => setEditBudget(row.budget)}
                onEdit={() => setEditBudget(row.budget)}
                profit={computeProfit(row)}
                setModalOpen={setModalOpen}
                setBudgetSelected={setBudgetSelected}
              />
            ))
          )}
        </tbody>
      </table>
    </div>
  </div>
);

const BudgetSectionConfidence = ({ title, description, rows, isAdmin, setEditBudget, setModalOpen, setBudgetSelected, computeProfit }) => (
  <div className="space-y-3">
    <div className="flex items-baseline gap-2 px-1">
      <h3 className="text-base font-semibold text-gray-700">{title}</h3>
      {description && <span className="text-xs text-gray-500">— {description}</span>}
    </div>

    <div className="w-full bg-white border border-gray-300 rounded">
      <table className="table-fixed w-full text-sm">
        <thead className="border-b border-gray-300">
          <tr>
            <th className="text-left font-semibold text-sm p-3 w-[22%]">Budget</th>
            <th className="text-left font-semibold text-sm px-3 w-[8%]">Confidence</th>
            <th className="text-left font-semibold text-sm p-2 w-[10%]">Amount</th>
            <th className="text-left font-semibold text-sm px-3 w-[10%]">Used</th>
            {isAdmin && <th className="text-left font-semibold text-sm px-3 w-[12%]">Real Cost</th>}
            {isAdmin && <th className="text-left font-semibold text-sm px-3 w-[10%]">Profitability</th>}
            <th className="text-left font-semibold text-sm px-3 w-[8%]">Status</th>
            <th className="text-left font-semibold text-sm px-3 w-[14%]">Progress</th>
            {isAdmin && <th className="text-left font-semibold text-sm px-3 w-[6%]"></th>}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={isAdmin ? 9 : 7} className="p-4 text-sm text-gray-500 text-center">No {title.toLowerCase()} budgets</td>
            </tr>
          ) : (
            rows.map((row, index) => (
              <BudgetRowConfidence
                key={index}
                item={row}
                isAdmin={isAdmin}
                onNavigate={() => setEditBudget(row.budget)}
                onEdit={() => setEditBudget(row.budget)}
                profit={computeProfit(row)}
                setModalOpen={setModalOpen}
                setBudgetSelected={setBudgetSelected}
              />
            ))
          )}
        </tbody>
      </table>
    </div>
  </div>
);

const BudgetRow = ({ item, isAdmin, onNavigate, onEdit, profit, setModalOpen, setBudgetSelected }) => {
  const [showActions, setShowActions] = useState(false);
  const actionsRef = React.useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (actionsRef.current && !actionsRef.current.contains(event.target)) setShowActions(false);
    }
    if (showActions) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showActions]);

  const userCost = item.activities.reduce((acc, cur) => acc + cur.cost, 0);
  const bankCost = item.banks.filter((bank) => bank.category !== "INCOME" && bank.category !== "DISBURSEMENT").reduce((acc, bank) => acc - bank.amount, 0);
  const realCost = userCost + bankCost;
  const percentageUsed = item.budget.amount ? (item.budget.amountUsed / item.budget.amount) * 100 : 0;

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });
  };

  const typeClasses = {
    project: "bg-green-100 text-green-800",
    overbudget: "bg-red-100 text-red-800",
    admin: "bg-purple-100 text-purple-800",
  };

  const getBarColor = (pct) => {
    if (pct >= 100) return "bg-red-600";
    if (pct >= 80) return "bg-orange-500";
    if (pct >= 60) return "bg-yellow-500";
    return "bg-blue-500";
  };

  return (
    <tr className={classNames("border-t border-gray-200 hover:bg-gray-50 cursor-pointer", item.budget.status === "inactive" && "opacity-50")} onClick={onNavigate}>
      <td className="text-left p-2 text-sm">
        <div className="font-semibold">{item.budget.name}</div>
        <div className="text-xs text-gray-500">{formatDate(item.budget.startAt)} - {formatDate(item.budget.endAt)}</div>
      </td>
      <td className="p-2 text-sm">
        <span className={`capitalize px-2 py-1 rounded-full text-xs font-medium ${typeClasses[item.budget.type] || "bg-gray-100 text-gray-800"}`}>{item.budget.type}</span>
      </td>
      <td className="text-left p-2 text-sm font-medium">{(item.budget.amount || 0).toLocaleString("fr-FR", { style: "currency", currency: "EUR" })}</td>
      <td className="text-left p-2 text-sm">
        <div className="font-medium">{(item.budget.amountUsed || 0).toLocaleString("fr-FR", { style: "currency", currency: "EUR" })}</div>
      </td>
      {isAdmin && (
        <td className="text-left p-2 text-sm">
          <div className="font-medium">{(realCost || 0).toLocaleString("fr-FR", { style: "currency", currency: "EUR" })}</div>
          <div className="text-xs text-gray-500">User: {(userCost || 0).toLocaleString("fr-FR", { style: "currency", currency: "EUR" })}</div>
          <div className="text-xs text-gray-500">Bank: {(bankCost || 0).toLocaleString("fr-FR", { style: "currency", currency: "EUR" })}</div>
        </td>
      )}
      {isAdmin && (
        <td className="text-left p-2 text-sm">
          <div className={`font-medium ${profit >= 0 ? "text-green-700" : "text-red-700"}`}>{(profit || 0).toLocaleString("fr-FR", { style: "currency", currency: "EUR" })}</div>
          <div className="text-xs text-gray-500">
            {profit && item.budget.amountUsed ? ((profit / (item.budget.amountUsed > item.budget.amount ? item.budget.amount : item.budget.amountUsed)) * 100).toFixed(2) : 0}%
          </div>
        </td>
      )}
      <td className="p-2 text-sm">
        {item.budget.signed_at ? (
          <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium">Signed</span>
        ) : (
          <span className="bg-red-100 text-red-800 px-2 py-1 rounded-full text-xs font-medium">Not Signed</span>
        )}
      </td>
      <td className="p-2 text-sm">
        <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
          <div className={`h-full rounded-full transition-all duration-300 ${getBarColor(percentageUsed)}`} style={{ width: `${Math.min(percentageUsed, 100)}%` }} />
        </div>
        <div className="text-xs text-gray-500 mt-1">{Math.round(percentageUsed)}%</div>
      </td>
      {isAdmin && (
        <td className="text-left p-2 text-sm" onClick={(e) => e.stopPropagation()}>
          <div className="relative">
            <button className="p-1 rounded hover:bg-gray-100" onClick={() => setShowActions((p) => !p)}>
              <MdMoreVert className="text-gray-600" />
            </button>
            {showActions && (
              <div className="absolute right-0 mt-1 bg-white shadow-lg rounded-md border border-gray-200 z-10 w-40" ref={actionsRef}>
                <button className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm flex items-center gap-2" onClick={() => { setShowActions(false); onEdit(); }}>
                  <MdModeEdit /> Edit
                </button>
                <button className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm flex items-center gap-2" onClick={() => { setBudgetSelected(item.budget); setModalOpen(true); }}>
                  <MdFilePresent /> Generate invoice
                </button>
              </div>
            )}
          </div>
        </td>
      )}
    </tr>
  );
};

const BudgetRowConfidence = ({ item, isAdmin, onNavigate, onEdit, profit, setModalOpen, setBudgetSelected }) => {
  const [showActions, setShowActions] = useState(false);
  const actionsRef = React.useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (actionsRef.current && !actionsRef.current.contains(event.target)) setShowActions(false);
    }
    if (showActions) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showActions]);

  const userCost = item.activities.reduce((acc, cur) => acc + cur.cost, 0);
  const bankCost = item.banks.filter((bank) => bank.category !== "INCOME" && bank.category !== "DISBURSEMENT").reduce((acc, bank) => acc - bank.amount, 0);
  const realCost = userCost + bankCost;
  const percentageUsed = item.budget.amount ? (item.budget.amountUsed / item.budget.amount) * 100 : 0;

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });
  };

  const getBarColor = (pct) => {
    if (pct >= 100) return "bg-red-600";
    if (pct >= 80) return "bg-orange-500";
    if (pct >= 60) return "bg-yellow-500";
    return "bg-blue-500";
  };

  return (
    <tr className={classNames("border-t border-gray-200 hover:bg-gray-50 cursor-pointer", item.budget.status === "inactive" && "opacity-50")} onClick={onNavigate}>
      <td className="text-left p-2 text-sm">
        <div className="font-semibold">{item.budget.name}</div>
        <div className="text-xs text-gray-500">{formatDate(item.budget.startAt)} - {formatDate(item.budget.endAt)}</div>
      </td>
      <td className="p-2 text-sm">
        <span className="text-sm font-medium text-gray-700">{item.budget.confidence ? `${item.budget.confidence}%` : "—"}</span>
      </td>
      <td className="text-left p-2 text-sm font-medium">{(item.budget.amount || 0).toLocaleString("fr-FR", { style: "currency", currency: "EUR" })}</td>
      <td className="text-left p-2 text-sm">
        <div className="font-medium">{(item.budget.amountUsed || 0).toLocaleString("fr-FR", { style: "currency", currency: "EUR" })}</div>
      </td>
      {isAdmin && (
        <td className="text-left p-2 text-sm">
          <div className="font-medium">{(realCost || 0).toLocaleString("fr-FR", { style: "currency", currency: "EUR" })}</div>
          <div className="text-xs text-gray-500">User: {(userCost || 0).toLocaleString("fr-FR", { style: "currency", currency: "EUR" })}</div>
          <div className="text-xs text-gray-500">Bank: {(bankCost || 0).toLocaleString("fr-FR", { style: "currency", currency: "EUR" })}</div>
        </td>
      )}
      {isAdmin && (
        <td className="text-left p-2 text-sm">
          <div className={`font-medium ${profit >= 0 ? "text-green-700" : "text-red-700"}`}>{(profit || 0).toLocaleString("fr-FR", { style: "currency", currency: "EUR" })}</div>
          <div className="text-xs text-gray-500">
            {profit && item.budget.amountUsed ? ((profit / (item.budget.amountUsed > item.budget.amount ? item.budget.amount : item.budget.amountUsed)) * 100).toFixed(2) : 0}%
          </div>
        </td>
      )}
      <td className="p-2 text-sm">
        {item.budget.signed_at ? (
          <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium">Signed</span>
        ) : (
          <span className="bg-red-100 text-red-800 px-2 py-1 rounded-full text-xs font-medium">Not Signed</span>
        )}
      </td>
      <td className="p-2 text-sm">
        <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
          <div className={`h-full rounded-full transition-all duration-300 ${getBarColor(percentageUsed)}`} style={{ width: `${Math.min(percentageUsed, 100)}%` }} />
        </div>
        <div className="text-xs text-gray-500 mt-1">{Math.round(percentageUsed)}%</div>
      </td>
      {isAdmin && (
        <td className="text-left p-2 text-sm" onClick={(e) => e.stopPropagation()}>
          <div className="relative">
            <button className="p-1 rounded hover:bg-gray-100" onClick={() => setShowActions((p) => !p)}>
              <MdMoreVert className="text-gray-600" />
            </button>
            {showActions && (
              <div className="absolute right-0 mt-1 bg-white shadow-lg rounded-md border border-gray-200 z-10 w-40" ref={actionsRef}>
                <button className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm flex items-center gap-2" onClick={() => { setShowActions(false); onEdit(); }}>
                  <MdModeEdit /> Edit
                </button>
                <button className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm flex items-center gap-2" onClick={() => { setBudgetSelected(item.budget); setModalOpen(true); }}>
                  <MdFilePresent /> Generate invoice
                </button>
              </div>
            )}
          </div>
        </td>
      )}
    </tr>
  );
};

// =====================================================
// HELPERS
// =====================================================

function calculateMonthlyData(budgets, activities, banks) {
  const activeBudgets = budgets.filter((b) => b.status === "active");
  const allMonths = new Set();
  const currentDate = new Date();

  activeBudgets.forEach((budget) => {
    const start = new Date(budget.startAt);
    const end = new Date(budget.endAt);
    for (let date = new Date(start); date <= end; date.setMonth(date.getMonth() + 1)) {
      allMonths.add(new Date(date.getFullYear(), date.getMonth(), 1).getTime());
    }
  });

  [...activities, ...banks].forEach((item) => {
    const d = new Date(item.date);
    allMonths.add(new Date(d.getFullYear(), d.getMonth(), 1).getTime());
  });

  const sortedMonths = Array.from(allMonths)
    .sort((a, b) => a - b)
    .map((t) => new Date(t));

  let cumulativeExpected = 0;
  let cumulativeActual = 0;

  return sortedMonths.map((monthDate) => {
    const month = monthDate.toLocaleString("default", { month: "long", year: "numeric" });
    const isInFuture = monthDate > currentDate;

    let expectedTotal = 0;
    activeBudgets.forEach((budget) => {
      const startDate = new Date(budget.startAt);
      startDate.setDate(1);
      startDate.setHours(0, 0, 0, 0);
      const endDate = new Date(budget.endAt);
      endDate.setHours(23, 59, 59, 999);
      const monthStartDate = new Date(monthDate);
      monthStartDate.setDate(1);
      monthStartDate.setHours(0, 0, 0, 0);
      const isWithinRange = monthStartDate >= startDate && monthStartDate <= endDate;
      const totalMonths = (endDate.getFullYear() - startDate.getFullYear()) * 12 + (endDate.getMonth() - startDate.getMonth()) + 1;
      if (isWithinRange) expectedTotal += budget.amount / totalMonths;
    });

    const monthActivities = activities.filter((a) => {
      const d = new Date(a.date);
      return d.getMonth() === monthDate.getMonth() && d.getFullYear() === monthDate.getFullYear();
    });
    const monthBanks = banks.filter((b) => {
      const d = new Date(b.date);
      return d.getMonth() === monthDate.getMonth() && d.getFullYear() === monthDate.getFullYear();
    });

    const actualTotal = isInFuture ? 0 : monthActivities.reduce((sum, a) => sum + Math.abs(a.value), 0) + monthBanks.reduce((sum, b) => sum + Math.abs(b.amount), 0);
    cumulativeExpected += expectedTotal;
    cumulativeActual += actualTotal;

    return { month, cumulativeExpected, cumulativeActual, isInFuture };
  });
}

const BudgetChart = ({ monthlyData }) => (
  <div className="h-64 w-full">
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart data={monthlyData}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="month" />
        <YAxis tickFormatter={(value) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", notation: "compact" }).format(value)} />
        <RechartsTooltip formatter={(value, name) => [new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(value), name]} />
        <Legend />
        <Bar dataKey="cumulativeActual" name="Cumulative Actual Expenses" fill="#3B82F6" />
        <Bar dataKey="cumulativeExpected" name="Cumulative Expected Budget" fill="#9CA3AF" />
      </ComposedChart>
    </ResponsiveContainer>
  </div>
);

const ActivitiesAndBanks = ({ activitiesByUser, banksByName }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
    <div>
      <h4 className="text-md font-medium mb-2">Activities by User</h4>
      <div className="space-y-2">
        {activitiesByUser.length === 0 ? (
          <p className="text-gray-400 text-sm">No activities</p>
        ) : (
          activitiesByUser.map((u) => (
            <div key={u.userName} className="flex items-center justify-between p-2 bg-white rounded-md border">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-xs font-bold text-blue-600">
                  {u.userAvatar ? <img src={u.userAvatar} alt={u.userName} className="w-8 h-8 rounded-full" /> : u.userName.split(" ").map((n) => n[0]).join("").slice(0, 2)}
                </div>
                <div className="text-sm">
                  <div className="font-medium">{u.userName}</div>
                  <div className="text-gray-400 text-xs">{u.userJobTitle || "No position"}</div>
                </div>
              </div>
              <div className="text-sm text-right">
                <div className="font-medium">{u.totalCost.toLocaleString("fr-FR", { style: "currency", currency: "EUR" })}</div>
                <div className="text-gray-400 text-xs">{u.totalDays.toFixed(2)} days</div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
    <div>
      <h4 className="text-md font-medium mb-2">Bank Expenses</h4>
      <div className="space-y-2">
        {banksByName.length === 0 ? (
          <div className="text-center py-4">
            <p className="text-gray-400 text-sm">No bank expenses</p>
            <p className="text-gray-300 text-xs">All expenses via activities</p>
            <p className="text-gray-400 text-sm font-medium mt-1">0 €</p>
          </div>
        ) : (
          banksByName.map((bank) => (
            <div key={bank.name} className="flex items-center justify-between p-2 bg-white rounded-md border">
              <div className="flex items-center space-x-2">
                <span>💳</span>
                <div className="text-sm">
                  <div>{bank.name}</div>
                  <div className="text-gray-400 text-xs">{bank.count} transaction{bank.count > 1 ? "s" : ""}</div>
                </div>
              </div>
              <div className="text-sm font-medium">{bank.totalAmount.toLocaleString("fr-FR", { style: "currency", currency: "EUR" })}</div>
            </div>
          ))
        )}
      </div>
    </div>
  </div>
);

function buildActivitiesByUser(activities) {
  const map = {};
  for (const activity of activities) {
    const userName = activity.userName || "Unknown User";
    if (!map[userName]) {
      map[userName] = { userName, userAvatar: activity.userAvatar, totalCost: 0, totalDays: 0, userJobTitle: activity.userJobTitle };
    }
    map[userName].totalCost += Math.abs(activity.value);
    map[userName].totalDays += activity.total / 8;
  }
  return Object.values(map).sort((a, b) => b.totalCost - a.totalCost);
}

function buildBanksByName(banks) {
  const filtered = (banks || []).filter((b) => b.category !== "INCOME" && b.category !== "DISBURSEMENT");
  const map = {};
  for (const bank of filtered) {
    const name = bank.name || "Unknown";
    if (!map[name]) map[name] = { name, totalAmount: 0, count: 0 };
    map[name].totalAmount += Math.abs(bank.amount);
    map[name].count += 1;
  }
  return Object.values(map).sort((a, b) => b.totalAmount - a.totalAmount);
}

const GenerateInvoiceModal = ({ modalOpen, setModalOpen, budgetSelected }) => {
  const [values, setValues] = useState({ name: budgetSelected?.name || "" });
  const navigate = useNavigate();

  const getInvoiceItems = async (budget) => {
    if (budget.facturation_type === "regie") {
      return [{
        name: budget.projectName || "Régie",
        price: budget.value || 0,
        quantity: 1,
        unit: "budget",
        totalPrice: budget.value || 0,
        description: "Prestation réalisée en régie sur la période convenue, facturation basée sur le temps passé et les ressources mobilisées.",
      }];
    }
    if (budget.facturation_type === "forfait") {
      const { ok, data } = await api.post(`/roadlog_item/search`, { budget_id: budget._id, statuses: ["TODO", "IN_PROGRESS", "DONE", "ARCHIVED"] });
      if (!ok) { toast.error("Failed to generate invoice"); return null; }
      return data.map((epic) => ({
        name: epic.title, description: epic.module || "", logo: epic.project_logo || "",
        price: epic.tjm, quantity: epic.days_required, unit: "days",
        totalPrice: Number((epic.days_required * epic.tjm || 0).toFixed(2)),
      }));
    }
    return [];
  };

  const generateInvoice = async () => {
    const itemsInvoice = await getInvoiceItems(budgetSelected);
    if (!itemsInvoice) return;
    const invoice = await api.post(`/invoice/`, {
      project_id: budgetSelected.projectId,
      budgetId: budgetSelected._id,
      name: values.name,
      date: values.date,
      itemsInvoice,
    });
    toast.success("Invoice generated successfully");
    navigate(`/invoice/${invoice.data._id}/editorbis`);
  };

  useEffect(() => {
    if (!budgetSelected) return;
    const currentDate = new Date(Date.UTC(new Date().getFullYear(), new Date().getMonth(), 1));
    setValues({ name: budgetSelected.name || "", date: currentDate });
  }, [budgetSelected]);

  return (
    <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} className="w-2/3">
      <div className="px-16 py-12 space-y-8">
        <h2 className="text-xl font-semibold mb-4">Create New Invoice</h2>
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700" htmlFor="invoice-name">Invoice name</label>
          <input id="invoice-name" value={values.name} onChange={(e) => setValues({ ...values, name: e.target.value })} placeholder="Invoice name" className="input" />
        </div>
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700" htmlFor="invoice-date">Invoice Date</label>
          <SelectMonth key={budgetSelected?._id} id="invoice-date" placeholder="Select month" indexDefaultValue={0} start={0} value={values.date} onChange={(e) => setValues({ ...values, date: e.target.value })} />
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button className="gray-btn" onClick={() => setModalOpen(false)}>Cancel</button>
          <button className="blue-btn" onClick={() => generateInvoice()}>Create</button>
        </div>
      </div>
    </Modal>
  );
};

